/**
 * Waar hangt welke krabbel aan de paal.
 *
 * De paal is een raster van vakjes: een paar banen naast elkaar, rijen naar
 * beneden. Elke krabbel hangt in precies één vakje. Je kunt hem oppakken en in een
 * ander vakje hangen; zit daar al iemand, dan ruilen ze van plek.
 *
 * Dat vakjesraster is er met opzet. Eerst mocht een krabbel overal neergelegd
 * worden, en dat werd een stapel: krabbels over elkaar heen, hartjes die elkaar
 * bedekken. Op een paal van 280 breed met krabbels van 104 is dat de enige
 * uitkomst. Met vakjes kies je nog steeds zelf waar iets hangt, maar kan het nooit
 * een rommeltje worden.
 *
 * Recht en saai wordt het niet: elke krabbel hangt een paar graden scheef en een
 * paar pixels uit het midden van zijn vakje, en dat volgt uit zijn id. Dus iedereen
 * ziet dezelfde paal, ook zonder dat er iets is opgeslagen.
 *
 * Deze module rekent alleen. Slepen en bewaren staat in logic/board.js.
 */

/** Een krabbel is vierkant, of dubbel breed als hij samen gemaakt is. */
export const NOTE_W = 104;
export const NOTE_H = 104;
export const WIDE_W = 216;

/**
 * De metalen paal in het midden, als er één baan met krabbels op hangt.
 *
 * Deze bodem geldt alleen op smalle schermen. Met drie banen rekent poleWidth zich
 * op 396 uit en doet dit getal niets. Ik heb hem een keer op 300 gezet om de paal
 * "een tikje breder" te maken, maar dat werkte alleen op de telefoon en juist de
 * verkeerde kant op: daar werd de paal 300 van de 390 pixels breed en las hij als
 * een muur. Wat de paal op desktop breder maakte was PAAL_RAND, niet dit.
 */
export const POLE_W = 280;

/** Hart-op-hart tussen twee vakjes. Ruimer dan een krabbel, dus er blijft lucht. */
export const LANE_PITCH = 120;
export const ROW_PITCH = 124;

/** Hoever een krabbel uit het hart van zijn vakje mag zwabberen. Minder dan de lucht. */
const ZWABBER = 7;

/** Lucht boven de eerste en onder de laatste rij. */
const TOP_PAD = 24;
const BOTTOM_PAD = 90;

/** Meer dan drie banen wordt te breed voor de paal. */
export const MAX_LANES = 3;

/**
 * En minder dan twee is geen paal meer.
 *
 * Eén baan zette alle krabbels kaarsrecht onder elkaar, en dan leest het als een
 * lijst in plaats van als iets dat om een paal hangt. Dat gebeurde vaker dan je
 * denkt: op een telefoon smaller dan 352 pixels, en op elk computervenster tussen
 * 761 en 919 breed, waar de zijbalk de paal aan de linkerkant afknijpt. Dat laatste
 * is precies een half laptopscherm.
 */
export const MIN_LANES = 2;

/**
 * Lucht tussen de buitenste krabbel en de rand van de paal.
 *
 * Ruimer dan de zestien die het was: met krap zestien pixels leek de buitenste
 * kolom van de paal af te vallen in plaats van eraan te hangen.
 */
const PAAL_RAND = 26;

/**
 * Hoe breed de paal moet zijn om de krabbels te dragen die eraan hangen.
 *
 * Met drie banen naast elkaar is 280 te smal: dan zweeft de buitenste kolom naast
 * het metaal in plaats van erop. De paal groeit dus mee met het aantal banen, maar
 * wordt nooit smaller dan hij was.
 */
export function poleWidth(lanes) {
  const nodig = (Math.max(1, lanes) - 1) * LANE_PITCH + NOTE_W + 2 * PAAL_RAND;
  return Math.max(POLE_W, Math.round(nodig));
}

/**
 * Een getal tussen 0 en 1 dat altijd hetzelfde is voor hetzelfde id. Zo ziet
 * iedereen dezelfde paal, ook zonder dat er ergens iets is opgeslagen.
 */
export function seed(id, salt) {
  const s = String(id || '') + '|' + salt;
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 10000) / 10000;
}

/**
 * Hoeveel banen passen er.
 *
 * `reach` is hoever een krabbel vanaf het hart van de paal mag komen zonder onder
 * de zijbalk te verdwijnen of van het scherm af te lopen. Een baan kost LANE_PITCH,
 * en de buitenste krabbel steekt daar nog een halve krabbel overheen.
 *
 * Gerekend met de breedste krabbel die er kan hangen, niet met de gewone. Een duo
 * krabbel is 216 breed in plaats van 104, en dat is meer dan een detail: op een
 * telefoon van 390 werd hier drie banen gekozen omdat een gewone krabbel er net in
 * paste, en viel de duo krabbel in de buitenste baan 42 pixels buiten het scherm.
 * Op mobiel snijdt `overflow-x: hidden` dat af, dus die krabbel was niet half te
 * zien maar half wég, en er was geen manier om erbij te komen.
 *
 * Onder de grens voor twee banen stopt het rekenen: MIN_LANES is de bodem, ook als
 * het krap is. Wat dan niet meer past is de duo-krabbel in de buitenste baan, en die
 * houdt `layout` binnen de rand met een klem. Dat is de goede ruil: een brede
 * krabbel die een stukje naar binnen hangt kun je nog zien en aanklikken, een paal
 * met alles op één rij is geen paal meer.
 */
export function laneCount(reach) {
  for (let n = MAX_LANES; n > MIN_LANES; n--) {
    const buitenrand = ((n - 1) / 2) * LANE_PITCH + WIDE_W / 2;
    if (buitenrand <= reach) return n;
  }
  return MIN_LANES;
}

/**
 * Een wenteltrap om de paal.
 *
 * De krabbels vormen één doorlopende reeks treden. Elke volgende trede hangt een
 * vaste stap lager en één baan opzij, en aan het eind gekomen draait de reeks terug:
 * links, midden, rechts, midden, links. Van voren gezien is dat precies wat een
 * wenteltrap doet die om een paal draait.
 *
 * Dit verving een gewone trap, waarbij de reeks aan het eind van elke rij met een
 * sprong terug naar links ging. Dat las als een raster met een schuine kop, niet
 * als iets dat om de paal heen loopt.
 */

/** Hoeveel een trede zakt.
 *
 *  Twee treden op dezelfde baan liggen twee treden uit elkaar, oftewel 176, en een
 *  krabbel is 104. Dat laat ruim zeventig pixels lucht. Ruimer maken kan, maar dat
 *  gaat één op één ten koste van hoe ver je moet scrollen. */
const TREDE = 88;

/**
 * Op welke baan trede `n` uitkomt.
 *
 * Een driehoeksgolf: 0,1,2,1,0,1,2,1,... Bij twee banen wordt dat 0,1,0,1 en bij
 * één baan valt er niets te draaien.
 */
export function stepLane(n, lanes) {
  if (lanes < 2) return 0;
  const periode = 2 * lanes - 2;
  const p = ((n % periode) + periode) % periode;
  return p < lanes ? p : periode - p;
}

/** Hoever een trede zakt ten opzichte van de vorige. */
export function stepPitch(lanes) {
  return lanes < 2 ? ROW_PITCH : TREDE;
}

/** Het hart van een baan, gerekend vanaf het hart van de paal. */
export function slotX(col, lanes) {
  return Math.round((col - (lanes - 1) / 2) * LANE_PITCH);
}

/** Waar trede `n` hangt. */
export function stepX(n, lanes) {
  return slotX(stepLane(n, lanes), lanes);
}

export function stepY(n, lanes) {
  return TOP_PAD + n * stepPitch(lanes);
}

/**
 * Welke trede hoort bij een punt op het vlak.
 *
 * De hoogte wijst de trede aan; van de drie treden daaromheen wint degene die het
 * dichtst bij je cursor ligt, zodat ook zijwaarts slepen doet wat je verwacht.
 */
export function nearestStep(x, y, lanes, aantal) {
  const ruw = Math.round((y - TOP_PAD) / stepPitch(lanes));
  const max = Math.max(0, (aantal || 1) - 1);
  let beste = Math.max(0, Math.min(max, ruw));
  let besteAfstand = Infinity;
  for (let n = ruw - 1; n <= ruw + 1; n++) {
    if (n < 0 || n > max) continue;
    const dx = stepX(n, lanes) - x;
    const dy = stepY(n, lanes) - y;
    const d = dx * dx + dy * dy;
    if (d < besteAfstand) { besteAfstand = d; beste = n; }
  }
  return beste;
}

/**
 * De hele paal in één keer uitgerekend.
 *
 * `overrides` is wat de gebruiker zelf heeft opgehangen: id naar tredenummer. Die
 * krijgen hun trede als eerste; de rest schuift in de treden die overblijven, in de
 * volgorde van de lijst.
 *
 * `reach` is hoever een krabbel vanaf het hart van de paal mag komen. Alleen brede
 * krabbels hebben er iets aan; laat je hem weg, dan klemt er niets.
 */
export function layout(items, isWide, overrides, lanes, reach) {
  const banen = Math.max(1, Math.min(MAX_LANES, lanes || 1));

  /**
   * Hoe ver het hart van een brede krabbel van het hart van de paal mag liggen.
   *
   * Een duo-krabbel is 216 breed terwijl de banen 120 uit elkaar liggen, dus hij
   * hangt altijd een stuk over zijn buren heen. Dat is inherent aan een dubbele
   * plaat en het ziet er ook prima uit. Wat niet mag is dat hij van het scherm
   * loopt, want `overflow-x: hidden` snijdt hem dan af en er is geen manier om erbij
   * te komen. Vandaar deze klem: raakt zijn buitenrand de grens, dan schuift hij
   * naar binnen tot hij er net in valt.
   *
   * Hij grijpt vooral in bij twee banen op een krap scherm, en verder alleen om de
   * zwabber van zeven pixels te fatsoeneren als een brede krabbel toevallig naar
   * buiten zwabbert.
   */
  const breedMax = typeof reach === 'number' ? Math.max(0, Math.round(reach - WIDE_W / 2)) : Infinity;
  const spots = new Map();
  const bezet = new Set();

  // Eerst wie een eigen trede heeft. Twee krabbels op dezelfde trede kan niet, dus
  // wie het eerst komt houdt hem; de ander schuift zo meteen aan bij de rest.
  for (const g of items) {
    const eigen = overrides && overrides[g.id];
    if (!Number.isInteger(eigen) || eigen < 0) continue;
    if (bezet.has(eigen)) continue;
    bezet.add(eigen);
    spots.set(g.id, { step: eigen, moved: true });
  }

  // En dan de rest, op de eerstvolgende vrije trede.
  let n = 0;
  for (const g of items) {
    if (spots.has(g.id)) continue;
    while (bezet.has(n)) n++;
    bezet.add(n);
    spots.set(g.id, { step: n, moved: false });
  }

  // Nu pas naar pixels, zodat de zwabber overal gelijk werkt.
  let onder = TOP_PAD;
  for (const g of items) {
    const s = spots.get(g.id);
    s.wide = !!isWide(g);
    s.col = stepLane(s.step, banen);
    s.x = stepX(s.step, banen) + Math.round((seed(g.id, 'x') - 0.5) * 2 * ZWABBER);
    if (s.wide && Math.abs(s.x) > breedMax) s.x = Math.sign(s.x) * breedMax;
    s.y = stepY(s.step, banen) + Math.round((seed(g.id, 'y') - 0.5) * 2 * ZWABBER);
    onder = Math.max(onder, s.y + NOTE_H);
  }

  return { spots, lanes: banen, height: Math.round(onder + BOTTOM_PAD) };
}
